import type { InboundConfigResponse } from '@/client/types.gen'
import {
  getInboundConfigAppOrganizationOrganizationsMeInboundConfigGetOptions,
  updateInboundConfigAppOrganizationOrganizationsMeInboundConfigPutMutation,
} from '@/client/@tanstack/react-query.gen'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { CopyButton } from '@/components/copybutton'
import { useSettingsSectionViewed } from '@/hooks/use-analytics'
import { useAuth } from '@/hooks/use-auth'
import { useToast } from '@/hooks/use-toast'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { AlertTriangle, Check, Loader2, Plus, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { SettingsPageSkeleton } from './-components/skeleton'

export const Route = createFileRoute('/_authed/_user/settings/order-inbox')({
  component: OrderInboxSettingsPage,
  pendingComponent: SettingsPageSkeleton,
  beforeLoad: ({ context }) => {
    if (!context.auth.canUseOrderInbox) {
      throw redirect({ to: '/' })
    }
    return {
      getTitle: () => 'Order Inbox',
      getDescription: () => 'Configure the email address that captures incoming order emails',
    }
  },
})

const EMAIL_PATTERN = /^[^\s@]+@[^\s@.]+\.[^\s@]+$/

interface ApiError {
  status?: number
  detail?: string | Array<{ msg?: string }>
  response?: { status?: number, data?: { detail?: string | Array<{ msg?: string }> } }
}

function getErrorDetail(error: ApiError): string | undefined {
  const detail = error.detail ?? error.response?.data?.detail
  if (typeof detail === 'string')
    return detail
  if (Array.isArray(detail) && detail.length > 0)
    return detail[0]?.msg
  return undefined
}

function OrderInboxSettingsPage() {
  useSettingsSectionViewed('order_inbox')
  const { isOrgAdmin } = useAuth()

  const configOptions = getInboundConfigAppOrganizationOrganizationsMeInboundConfigGetOptions()
  const {
    data: config,
    isLoading: configLoading,
    error: configError,
  } = useQuery({
    ...configOptions,
    enabled: isOrgAdmin,
  })

  if (!isOrgAdmin) {
    return (
      <Alert>
        <AlertDescription>
          You don&apos;t have permission to view this page. Please ask an admin to configure the order inbox.
        </AlertDescription>
      </Alert>
    )
  }

  if (configLoading) {
    return <SettingsPageSkeleton />
  }

  if (configError || !config) {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          {configError instanceof Error ? configError.message : 'Failed to load configuration'}
        </AlertDescription>
      </Alert>
    )
  }

  // Keying on the saved-config snapshot resets local form state after each save.
  const formKey = `${config.allowlistEnabled ? '1' : '0'}|${config.allowlist.join(',')}`
  return <OrderInboxForm key={formKey} config={config} />
}

interface OrderInboxFormProperties {
  config: InboundConfigResponse
}

function OrderInboxForm({ config }: OrderInboxFormProperties) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const configOptions = getInboundConfigAppOrganizationOrganizationsMeInboundConfigGetOptions()

  const [allowlistEnabled, setAllowlistEnabled] = useState(config.allowlistEnabled)
  const [allowlist, setAllowlist] = useState<string[]>(config.allowlist)
  const [emailDraft, setEmailDraft] = useState('')
  const [emailError, setEmailError] = useState<string>('')
  const [submitError, setSubmitError] = useState<string>('')

  const { mutateAsync: updateConfig, isPending: isSaving } = useMutation({
    ...updateInboundConfigAppOrganizationOrganizationsMeInboundConfigPutMutation(),
    onSuccess: (response) => {
      queryClient.setQueryData(configOptions.queryKey, response)
      setSubmitError('')
      toast({ title: 'Saved', variant: 'success' })
    },
    onError: (error) => {
      const apiError = error as ApiError
      const message = getErrorDetail(apiError) ?? 'An unexpected error occurred'
      setSubmitError(message)
      toast({
        title: 'Failed to save',
        description: message,
        variant: 'destructive',
      })
    },
  })

  const dirty = useMemo(() => {
    if (allowlistEnabled !== config.allowlistEnabled)
      return true
    if (allowlist.length !== config.allowlist.length)
      return true
    return allowlist.some((email, index) => email !== config.allowlist[index])
  }, [config, allowlistEnabled, allowlist])

  const handleAddEmail = () => {
    const trimmed = emailDraft.trim().toLowerCase()
    if (trimmed.length === 0)
      return
    if (!EMAIL_PATTERN.test(trimmed)) {
      setEmailError('Please enter a valid email address')
      return
    }
    if (allowlist.includes(trimmed)) {
      setEmailError('That email is already on the allowlist')
      return
    }
    setAllowlist([...allowlist, trimmed])
    setEmailDraft('')
    setEmailError('')
  }

  const handleRemoveEmail = (email: string) => {
    setAllowlist(allowlist.filter(e => e !== email))
  }

  const handleSave = async () => {
    setSubmitError('')
    await updateConfig({
      body: {
        allowlistEnabled,
        // Only send the allowlist when the toggle is on; the server treats
        // a disabled toggle as "no restrictions" and ignores the list.
        allowlist: allowlistEnabled ? allowlist : undefined,
      },
    })
  }

  const saveDisabled = isSaving || !dirty
  const allowlistEmpty = allowlistEnabled && allowlist.length === 0

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Inbound address</CardTitle>
          <CardDescription>
            Forward order emails to this address. Each forwarded message becomes a draft order.
            Contact SUPPLYCO support if you need this address changed.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <Label>Address</Label>
          <div className="flex items-center gap-2 rounded-md border bg-muted/40 px-3 py-2">
            <code className="flex-1 break-all text-sm">{config.address}</code>
            <CopyButton text={config.address} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Forwarder allowlist</CardTitle>
          <CardDescription>
            Restrict which email addresses are allowed to forward orders to your inbox.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-3">
            <Switch
              id="allowlistEnabled"
              checked={allowlistEnabled}
              onCheckedChange={setAllowlistEnabled}
            />
            <Label htmlFor="allowlistEnabled">Restrict who can forward</Label>
          </div>

          {!!allowlistEnabled && (
            <div className="space-y-3">
              {!!allowlistEmpty && (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    No one can forward yet. Add at least one email address below.
                  </AlertDescription>
                </Alert>
              )}

              {allowlist.length > 0 && (
                <ul className="space-y-1">
                  {allowlist.map(email => (
                    <li
                      key={email}
                      className="flex items-center justify-between rounded-md border px-3 py-2 text-sm"
                    >
                      <span className="break-all">{email}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        aria-label={`Remove ${email}`}
                        onClick={() => handleRemoveEmail(email)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </li>
                  ))}
                </ul>
              )}

              <div className="flex items-start gap-2">
                <div className="flex-1 space-y-1">
                  <Input
                    placeholder="name@example.com"
                    value={emailDraft}
                    type="email"
                    autoCapitalize="off"
                    autoCorrect="off"
                    spellCheck={false}
                    onChange={(event_) => {
                      setEmailDraft(event_.target.value)
                      setEmailError('')
                    }}
                    onKeyDown={(event_) => {
                      if (event_.key === 'Enter') {
                        event_.preventDefault()
                        handleAddEmail()
                      }
                    }}
                  />
                  {emailError.length > 0 && <p className="text-sm text-destructive">{emailError}</p>}
                </div>
                <Button type="button" variant="outline" onClick={handleAddEmail}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {submitError.length > 0 && (
        <Alert variant="destructive">
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>
      )}

      <div className="flex justify-end">
        <Button type="button" disabled={saveDisabled} onClick={handleSave}>
          {isSaving
            ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              )
            : (
                <>
                  <Check className="mr-2 h-4 w-4" />
                  Save changes
                </>
              )}
        </Button>
      </div>
    </div>
  )
}
